import { useState } from "react";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import {
  useGetEmployeeQuery,
  useGetRipeningDashboardQuery,
  useGetRipeningSummaryQuery,
  useSearchRipeningBarChartQuery,
} from "./ripeingDashboardApi";
import AddRipeningEmployeeData from "./AddRipeningEmployeeData";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate } from "../../../helpers/Utils";
import DashboardSection from "../../../features/dashboard/components/DashboardSection";
import DashboardCard from "../../../features/dashboard/components/DashboardCard";
import "../../../features/dashboard/components/dashboard-sections.css";
import { SnowflakeIconSvg } from "../../../svgFiles/SnowflakeIconSvg";
import { RipeningIconSvg } from "../../../svgFiles/RipeningIconSvg";
import { SortingIconSvg } from "../../../svgFiles/SortingIconSvg";
import { BarChartIconSvg } from "../../../svgFiles/BarChartIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { TrendingUpIconSvg } from "../../../svgFiles/TrendingUpIconSvg";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

const MetricBox = ({ label, value, unit }) => (
  <div className="db-evap-box">
    <div className="db-evap-label">{label}</div>
    <div className="db-evap-value">
      {value ?? "0"}{unit && <span className="db-metric-unit"> {unit}</span>}
    </div>
  </div>
);

const RipeningDashboard = () => {

  const { _t } = useTranslator();  
  const currentDate = getCurrentDate('YYYY-MM-DD');
  const [date, setDate] = useState(currentDate);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const tab = "dashboard";


  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
  };

  const { data: summaryData } = useGetRipeningSummaryQuery({ tab, date });


  // Chart queries
  const { data: barChartData } = useSearchRipeningBarChartQuery({
    start_date: startDate,
    end_date: endDate,
  });

  //  ############## RIPENING DASHBOARD OVERVIEW ################ 


  const {
    data : overviewData ,
    isLoading : overviewLoading ,
    isFetching : overviewFetching ,
    isError : overviewError
  } = useGetRipeningDashboardQuery({
    startDate : startDate ,
    endDate : endDate
  });


  const cold_storage_overview = overviewData?.data?.cold_storage_overview ?? [];
  const ripening_room_overview = overviewData?.data?.ripening_room_overview ?? [];
  const sorting_overview = overviewData?.data?.sorting_overview ?? [];
  const metrics = overviewData?.data?.metrics ?? [];

  // Cold Storage Overview Items
  const coldStorageListItems = [
    {
      label: _t("total_pallets", "Total Pallets"),
      value: cold_storage_overview?.total_pallet_with_36_cases || "0",
    },
    {
      label: _t("total_cases_cold_storage", "Total Cases in Cold Storage"),
      value: cold_storage_overview?.total_cases_in_cold_storage || "0",
    },
    {
      label: _t("total_fruit_entered_kg", "Total Fruit Entered / KG"),
      value: cold_storage_overview?.total_fruit_kg_in_cold_storage || "0",
      unit: " KG",
    },
  ];

  // Ripening Rooms Overview Items
  const ripeningRoomsListItems = [
    {
      label: _t("total_cases_ripening", "Total Cases Entered in Rooms"),
      value: ripening_room_overview?.total_cases_in_ripening || 0,
      color: "orange",
    },
    {
      label: _t("total_kg_in_ripening", "Total Kg in Ripening"),
      value: ripening_room_overview?.total_kg_in_ripening || 0,
      unit: " KG",
      color: "orange",
    },
  ];
// Sorting Overview Items - Expanded
  const sortingListItems = [
    {
      label: _t("cases_in_sorting", "Cases in Sorting"),
      value: sorting_overview?.total_cases_entered_in_sorting,      
    },
    {
      label: _t("total_kg_in_sorting", "Total Kg in Sorting"),
      value: sorting_overview?.total_fruit_kg_entered_in_sorting,
    },
    {
      label: _t("cases_entered_processing", "Cases Entered Processing"),
      value: sorting_overview?.total_cases_entered_in_processing || "0",
    },
    {
      label: _t("kg_entered_processing", "KG Entered Processing"),
      value: sorting_overview?.total_fruit_kg_entered_in_processing || "0",
    },
    {
      label: _t("cases_rejected_label", "Cases Rejected"),
      value: sorting_overview?.total_cases_rejected || "0",
    },
    {
      label: _t("kg_rejected_label", "KG Rejected"),
      value: sorting_overview?.total_kg_rejected || "0",
    },
    {
      label: _t("kg_evaporated_label", "KG Evaporated"),
      value: sorting_overview?.total_evaporated_kg || "0",
    },
    {
      label: _t("kg_returned_label", "KG Returned"),
      value: sorting_overview?.total_kg_returned || "0",
    },    
  ];
  // These Metrics Items - Grouped
  const theseMetricsItems = {
    kgPerCase: [
      {
        label: _t("kg_per_case_entered_sorting", "KG Per Case Entered Sorting"),
        value: metrics?.kg_per_case_entered_sorting || 0,
        unit: "kg/case",
        theme: "blue",
      },
      {
        label: _t("kg_per_case_entered_processing", "KG Per Case Entered Processing"),
        value: metrics?.kg_per_case_entered_processing || 0,
        unit: "kg/case",
        theme: "green",
      },
    ],
    percentHeavier: [
      {
        label: _t("percent_heavier_case_into_processing", "Percent Heavier Case Into Processing"),
        value: summaryData?.data.percent_heavier_case_into_processing || 0,
        unit: "%",
        theme: "orange",
      },
      // {
      //   label: _t("percent_heavier_28kg_baseline", "Percent Heavier at 28 KG Baseline Entering Processing"),
      //   value: summaryData?.data.percent_heavier_28kg_baseline || 0,
      //   unit: "%",
      //   theme: "orange",
      // },
    ],
  };

  const casesLabel = _t("cases", "Cases");

  // Room Data Bar Chart Options
  const roomCapacity = _t("room_capacity_section", "Room Capacity");
  const totalCasesEntered = _t("total_cases_entered_room", "Total Cases Entered");
  const emptyRoomSpace = _t("empty_space_in_room", "Empty Space in Room");

  const optionsBar = {
    chart: { type: "column" },
    title: { text: null, align: "left" },
    xAxis: {
      categories: barChartData?.data?.categories,
      labels: {
        style: { fontSize: "12px", color: "#8C97A7" },
      },
    },
    yAxis: { title: { text: casesLabel } },
    legend: {
      align: "center",
      verticalAlign: "top",
      layout: "horizontal",
    },
    tooltip: { shared: true },
    plotOptions: {
      column: {
        grouping: true,
        borderWidth: 0,
        dataLabels: { enabled: false },
      },
    },
    series: [
      {
        name: roomCapacity,
        data: barChartData?.data?.data.map((room) => room.cases_capacity),
        color: "#ffcf86",
      },
      {
        name: totalCasesEntered,
        data: barChartData?.data?.data.map((room) => room.cases_entered),
        color: "#78db80",
      },
      {
        name: emptyRoomSpace,
        data: barChartData?.data?.data.map((room) => room.empty_space),
        color: "#ff8e8e",
      },
    ],
  };


  return (
    <div>

      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="d-flex gap-2 align-items-center mb-4">
        <h5 className="m-0 flex-grow-1">{_t("ripening_overview", "Ripening Overview")}</h5>
        <DateRangeFilter setDate={setDate} onApplyRange={handleApplyRange} triggerId="ripening-date-range-filter" />
      </div>

      <div>
        {/* ── Cold Storage Overview Accordion ────────────────────── */}
        <DashboardSection title={
          <span className="d-flex align-items-center gap-2">
            <SnowflakeIconSvg size={18} color="#60a5fa" />
            {_t("cold_storage_overview", "Cold Storage Overview")}
            <span className="pf-live-badge" style={{ fontSize: 10, padding: "3px 9px" }}>
              <span className="pf-live-dot" />{_t("live_tracking", "Live Tracking")}
            </span>
          </span>
        }>
          <div className="db-evap-row p-0">
            {coldStorageListItems.map((item, i) => (
              <MetricBox
                key={i}
                label={item.label}
                value={item.value}
                unit={item.unit}
              />
            ))}
          </div>
        </DashboardSection>

        {/* ── Ripening Rooms Overview Accordion with Bar Chart ──────────────────── */}
        <DashboardSection title={
          <span className="d-flex align-items-center gap-2">
            <RipeningIconSvg size={18} color="#fb923c" />
            {_t("ripening_rooms_overview", "Ripening Rooms Overview")}
            <span className="pf-live-badge" style={{ fontSize: 10, padding: "3px 9px" }}>
              <span className="pf-live-dot" />{_t("live_tracking", "Live Tracking")}
            </span>
          </span>
        }>
          <div className="db-trash-grid mb-4 p-0">
            {ripeningRoomsListItems.map((item, i) => (
              <div key={i} className="db-trash-cell">
                <span>{item.label}</span>
                <span className={`db-trash-val ${item.color}`}>{item.value}{item.unit || ''}</span>
              </div>
            ))}
          </div>
          {/* Room Data Bar Chart */}
          <div className="cmn_card">
            <div className="overview_header d-flex gap-2 align-items-center">
              <h6 className="mb-0 flex-grow-1">
                <span className="d-flex align-items-center gap-2"><BarChartIconSvg size={16} color="#60a5fa" />{_t("room_data_overview_label", "Room Data Overview")}</span>
              </h6>
            </div>
            <div className="p-2 position-relative">
              <HighchartsReact highcharts={Highcharts} options={optionsBar} />
            </div>
          </div>
        </DashboardSection>
        {/* ── Sorting Overview Accordion with Pie Chart ─────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><SortingIconSvg size={18} color="#60a5fa" />{_t("sorting_overview", "Sorting Overview")}</span>}>
          <div className="pp-brokering-grid p-0">
            {sortingListItems.map((item, i) => (
              <div key={i} className="pp-brokering-box" style={{ backgroundColor: item.bgColor }}>
                <div className="pp-brokering-box-label">{item.label}</div>
                <div className="pp-brokering-box-value">{item.value}{item.unit || ''}</div>
              </div>
            ))}
          </div>
        </DashboardSection>
        {/* ── Metrics Overview Accordion ─────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><BarChartIconSvg size={18} color="#60a5fa" />{_t("metrics", "Metrics")}</span>}>
          <div className="db-two-col gap-3">
            {/* KG Per Case Group */}
            <DashboardCard title={_t("kg_per_case_per_fruit", "KG Per Case Per Fruit")} icon={<ScaleIconSvg size={16} color="#667085" />}>
              <div className="fqm-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                {theseMetricsItems.kgPerCase.map((item, i) => (
                  <div key={i} className={`pp-brokering-box bro-box-${item.theme}`}>
                    <div className="pp-brokering-box-label">{item.label}</div>
                    <div className="pp-brokering-box-value">{item.value} {item.unit}</div>
                  </div>
                ))}
              </div>
            </DashboardCard>

            {/* Percent Heavier Group */}
            <DashboardCard title={_t("percent_heavier", "Percent Heavier")} icon={<TrendingUpIconSvg size={16} color="#667085" />}>
              <div className="fqm-grid" style={{ gridTemplateColumns: "1fr" }}>
                {theseMetricsItems.percentHeavier.map((item, i) => (
                  <div key={i} className={`pp-brokering-box bro-box-${item.theme}`}>
                    <div className="pp-brokering-box-label">{item.label}</div>
                    <div className="pp-brokering-box-value">{item.value}{item.unit || ''}</div>
                  </div>
                ))}
              </div>
            </DashboardCard>
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default RipeningDashboard;
